"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { PranceLogo } from "~/components/assets/PranceLogo";
import { routes } from "~/lib/routes";

export default function ForgotPasswordPage() {
  const t = useTranslations("ForgotPasswordPage");
  const router = useRouter();

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    setSentEmail(email);

    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        setSuccess(true);
        return;
      }

      const data = (await res.json().catch(() => null)) as {
        error?: string;
      } | null;
      if (res.status === 429 || data?.error === "rate_limit_exceeded") {
        setError(t("errorRateLimit"));
      } else {
        setError(t("errorUnknown"));
      }
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "mt-1 block w-full rounded-md border border-transparent bg-sub px-3 py-2 text-sm text-fg outline-none transition-colors placeholder:text-muted focus:border-fg/20 focus:ring-2 focus:ring-fg/10";

  const btnClass =
    "w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50";

  return (
    <div className="grid h-screen grid-cols-2">
      <div className="center bg-[url(/assets/bg.png)] bg-cover bg-right p-8">
        <PranceLogo />
      </div>
      <div className="center flex-col">
        <div className="w-full max-w-sm space-y-6 px-8">
          <h1 className="font-medium text-2xl text-fg">{t("title")}</h1>
          {success ? (
            <div className="space-y-4">
              <p className="text-muted text-sm">{t("success")}</p>
              <button
                type="button"
                onClick={() =>
                  router.push(routes.auth.resetPassword(sentEmail))
                }
                className={btnClass}
              >
                {t("enterCode")}
              </button>
            </div>
          ) : (
            <>
              <p className="text-muted text-sm">{t("description")}</p>
              <form
                className="space-y-4"
                onSubmit={(e) => void handleSubmit(e)}
              >
                <div>
                  <label
                    htmlFor="email"
                    className="block font-medium text-fg text-sm"
                  >
                    {t("emailLabel")}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className={inputClass}
                  />
                </div>
                {error && <p className="text-error text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className={btnClass}
                >
                  {submitting ? t("submitting") : t("submit")}
                </button>
              </form>
            </>
          )}
          <p className="text-center text-sm">
            <Link
              href={routes.auth.signIn}
              className="text-muted hover:text-fg"
            >
              {t("backToSignIn")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
